/**
 * Lista de clientes — /clientes (painel de saude, fiel ao mockup fase-3).
 *
 * Cabecalho (breadcrumb + titulo + resumo + Exportar/Novo cliente) ·
 * 4 cards de saude (aneis Saudaveis/Atencao/Em risco + Top 10 LTV) ·
 * barra de filtro (busca + Filtros + abas por tier) · tabela com anel de
 * saude, cliente, health, ultimo contato, pedidos, LTV e status.
 *
 * Engajamento (score/tier/status/LTV/ultimo contato) vem do backend
 * (GET /api/comercial/clientes + /saude-resumo). Click numa linha ->
 * /clientes/:id. "Novo cliente" -> SlideOverPanel com Form.
 *
 * Usa classes canonicas (.cmp, .health-ring, .dtable, .badge, .tabchip,
 * .input-prefixed, .btn) — estilo bespoke fica em inline-styles com tokens.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiDownload,
  FiPlus,
  FiSearch,
  FiSliders,
  FiUsers,
} from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";

import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import {
  criarCliente,
  listarClientes,
  listarRepresentantes,
  obterSaudeResumoClientes,
} from "../../services/comercial";
import { possiveisDuplicados } from "../../services/crm";
import { buscarCep } from "../../services/enderecos";
import { PageContainer } from "../../styles/shared";

import ClienteForm from "./Form";
import HealthRing from "./HealthRing";
import { getItemTenant, setItemTenant } from "../../utils/tenantStorage";

// MT-4.7: filtros persistidos por tenant (getItemTenant migra a chave antiga).
const STORAGE_FILTROS = "dash-erp:clientes:filtros";

const ABAS = [
  { id: "todos", label: "Todos" },
  { id: "saudaveis", label: "Saudáveis" },
  { id: "atencao", label: "Atenção" },
  { id: "em_risco", label: "Em risco" },
];

const HEALTH_BADGE = {
  saudavel: { tone: "success", label: "Saudável" },
  atencao: { tone: "warning", label: "Atenção" },
  em_risco: { tone: "danger", label: "Em risco" },
  sem_score: { tone: "neutral", label: "—" },
};

// ---------- Filtros (persistencia + query string) ----------

function lerFiltros(searchParams) {
  const fromQs = {
    busca: searchParams.get("busca") || "",
    tipo: searchParams.get("tipo") || "",
    status: searchParams.get("status") || "ativos",
    saude: searchParams.get("saude") || "todos",
  };
  if (
    fromQs.busca ||
    searchParams.has("tipo") ||
    searchParams.has("status") ||
    searchParams.has("saude")
  ) {
    return fromQs;
  }
  try {
    const cache = JSON.parse(getItemTenant(STORAGE_FILTROS) || "{}");
    return { busca: "", tipo: "", status: "ativos", saude: "todos", ...cache };
  } catch {
    return { busca: "", tipo: "", status: "ativos", saude: "todos" };
  }
}

function salvarFiltros(filtros) {
  setItemTenant(STORAGE_FILTROS, JSON.stringify(filtros));
}

function statusToParams(status) {
  // "Inativos" trazia ativos e inativos juntos (`incluir_inativos`): quem
  // procurava o cliente desativado tinha de achá-lo no meio da lista toda.
  if (status === "inativos") return { ativo: false };
  if (status === "bloqueados") return { bloqueado: true };
  if (status === "todos") return { incluirInativos: true };
  return {}; // "ativos" (default)
}

// ---------- Formatadores ----------

function fmtBRLCheio(valor) {
  const n = Number(valor) || 0;
  return `R$ ${Math.round(n).toLocaleString("pt-BR")}`;
}

function fmtBRLCompacto(valor) {
  const n = Number(valor) || 0;
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `R$ ${Math.round(n / 1_000)}k`;
  return `R$ ${Math.round(n)}`;
}

function subContato(cliente) {
  const partes = [cliente.email, cliente.telefone].filter(Boolean);
  if (partes.length) return partes.join(" · ");
  const local = [cliente.cidade, cliente.tipo].filter(Boolean).join(" · ");
  return local || "—";
}

function avatarGradiente(nome) {
  let h = 0;
  for (const ch of nome || "x") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const a = (h % 8) + 1;
  // `>>> 3` (shift sem sinal): `>> 3` daria negativo p/ h > 2^31 -> var(--chart--5) invalido.
  let b = ((h >>> 3) % 8) + 1;
  if (b === a) b = (b % 8) + 1;
  return `linear-gradient(135deg, var(--chart-${a}), var(--chart-${b}))`;
}

function exportarCsv(clientes) {
  const headers = [
    "Nome", "Tipo", "Health", "Tier", "Último contato", "Pedidos", "LTV", "Status",
  ];
  const linhas = clientes.map((c) => [
    c.nome,
    c.tipo,
    c.health_score ?? "",
    c.health_tier || "",
    c.ultimo_contato || "",
    c.pedidos_count ?? 0,
    c.ltv ?? 0,
    c.status?.label || "",
  ]);
  const csv = [headers, ...linhas]
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? "");
          return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(";")
    )
    .join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `clientes_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 0);
}

// ---------- Sub-componentes ----------

function KpiSaude({ label, count, pct, scoreMedio, tier, danger }) {
  return (
    <div
      className="cmp"
      style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}
    >
      <HealthRing score={scoreMedio || 0} tier={tier} size={48} />
      <div>
        <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)" }}>
          {label}
        </div>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            fontSize: 22,
            letterSpacing: "-0.025em",
            color: danger ? "var(--danger-text)" : "var(--ink)",
          }}
        >
          {count}{" "}
          <span style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 500 }}>
            · {pct}%
          </span>
        </div>
      </div>
    </div>
  );
}

function KpiTop10({ valor }) {
  return (
    <div
      className="cmp"
      style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 999,
          background: "var(--accent-soft)",
          display: "grid",
          placeItems: "center",
          color: "var(--accent)",
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      </div>
      <div>
        <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)" }}>
          TOP 10 (LTV)
        </div>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            fontSize: 22,
            letterSpacing: "-0.025em",
            color: "var(--accent)",
          }}
        >
          {valor}
        </div>
      </div>
    </div>
  );
}

function HealthBadge({ tier }) {
  const cfg = HEALTH_BADGE[tier] || HEALTH_BADGE.sem_score;
  return (
    <span className={`badge ${cfg.tone} no-dot`} style={{ fontSize: 11 }}>
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status }) {
  if (!status) return <span className="badge neutral">—</span>;
  const semDot = status.label === "Reativar";
  return (
    <span className={`badge ${status.tone}${semDot ? " no-dot" : ""}`}>
      {status.label}
    </span>
  );
}

function PedidosCell({ cliente }) {
  const n = cliente.pedidos_count ?? 0;
  if (n === 0 && cliente.orcamento_aberto) {
    return (
      <>
        <strong>0</strong> <span className="muted">· orçamento aberto</span>
      </>
    );
  }
  return (
    <>
      <strong>{n}</strong> <span className="muted">{n === 1 ? "pedido" : "pedidos"}</span>
    </>
  );
}

// ---------- Pagina ----------

function ClientesLista() {
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filtros, setFiltros] = useState(() => lerFiltros(searchParams));
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [estado, setEstado] = useState({ loading: true, error: null, clientes: [] });
  const [resumo, setResumo] = useState(null);
  const [novoAberto, setNovoAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const podeCriar = isMaster || hasAllPermissions(["comercial:create"]);

  const carregar = useCallback(async () => {
    setEstado((prev) => ({ ...prev, loading: true, error: null }));
    const base = {
      busca: filtros.busca || undefined,
      tipo: filtros.tipo || undefined,
      ...statusToParams(filtros.status),
    };
    try {
      const [payload, resumoPayload] = await Promise.all([
        listarClientes(request, {
          ...base,
          saude: filtros.saude !== "todos" ? filtros.saude : undefined,
          ordering: "nome",
        }),
        obterSaudeResumoClientes(request, base),
      ]);
      const items = payload?.results || payload || [];
      setEstado({
        loading: false,
        error: null,
        clientes: Array.isArray(items) ? items : [],
      });
      setResumo(resumoPayload || null);
    } catch (err) {
      setEstado({
        loading: false,
        error: err?.message || "Erro ao carregar clientes.",
        clientes: [],
      });
    }
  }, [request, filtros.busca, filtros.tipo, filtros.status, filtros.saude]);

  useEffect(() => {
    carregar();
    salvarFiltros(filtros);
  }, [carregar, filtros]);

  const aplicarFiltro = (chave, valor) => {
    const novo = { ...filtros, [chave]: valor };
    setFiltros(novo);
    const usp = new URLSearchParams();
    Object.entries(novo).forEach(([k, v]) => {
      if (v && !(k === "status" && v === "ativos") && !(k === "saude" && v === "todos")) {
        usp.set(k, v);
      }
    });
    setSearchParams(usp, { replace: true });
  };

  const subHeader = useMemo(() => {
    if (!resumo) return "Carregando indicadores…";
    return `${resumo.ativos} ativos · ${resumo.em_risco} em risco · LTV médio ${fmtBRLCompacto(
      resumo.ltv_medio
    )}`;
  }, [resumo]);

  const tiers = resumo?.por_tier;

  // Funcao estavel: o ClienteForm recarrega a lista quando ela muda.
  const carregarParceiros = useCallback(
    () => listarRepresentantes(request, { pageSize: 200 }, { silentError: true }),
    [request]
  );

  const handleCriar = async (dados) => {
    setSalvando(true);
    try {
      // `silentError`: o form mostra o erro por campo; o toast so repetiria.
      const criado = await criarCliente(request, dados, { silentError: true });
      setNovoAberto(false);
      navigate(`/clientes/${criado.id}`);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <PageContainer>
      {/* Cabecalho */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div>
          <div className="breadcrumb">
            <span className="crumb">Início</span>
            <span className="crumb-sep">/</span>
            <span className="crumb">Comercial</span>
            <span className="crumb-sep">/</span>
            <span className="crumb active">Clientes</span>
          </div>
          <h1 className="page-title">Clientes</h1>
          <div className="page-sub">{subHeader}</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => exportarCsv(estado.clientes)}
            disabled={!estado.clientes.length}
          >
            <FiDownload aria-hidden="true" /> Exportar
          </button>
          {podeCriar && (
            <button type="button" className="btn btn--primary" onClick={() => setNovoAberto(true)}>
              <FiPlus aria-hidden="true" /> Novo cliente
            </button>
          )}
        </div>
      </div>

      {/* Cards de saude */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <KpiSaude
          label="SAUDÁVEIS"
          tier="saudavel"
          count={tiers?.saudaveis.count ?? 0}
          pct={tiers?.saudaveis.pct ?? 0}
          scoreMedio={tiers?.saudaveis.score_medio ?? 0}
        />
        <KpiSaude
          label="ATENÇÃO"
          tier="atencao"
          count={tiers?.atencao.count ?? 0}
          pct={tiers?.atencao.pct ?? 0}
          scoreMedio={tiers?.atencao.score_medio ?? 0}
        />
        <KpiSaude
          label="EM RISCO"
          tier="em_risco"
          danger
          count={tiers?.em_risco.count ?? 0}
          pct={tiers?.em_risco.pct ?? 0}
          scoreMedio={tiers?.em_risco.score_medio ?? 0}
        />
        <KpiTop10 valor={fmtBRLCompacto(resumo?.top10_ltv_total)} />
      </div>

      {/* Barra de filtro */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <div className="input-prefixed" style={{ height: 32, minWidth: 320 }}>
          <FiSearch aria-hidden="true" />
          <input
            placeholder="Buscar por nome, e-mail, telefone ou CPF/CNPJ (com ou sem máscara)…"
            value={filtros.busca}
            onChange={(e) => aplicarFiltro("busca", e.target.value)}
            aria-label="Buscar cliente"
          />
        </div>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => setFiltrosAbertos((v) => !v)}
          aria-expanded={filtrosAbertos}
        >
          <FiSliders aria-hidden="true" /> Filtros
        </button>
        <div className="tabchip-group" style={{ height: 28 }}>
          {ABAS.map((aba) => (
            <div
              key={aba.id}
              className={`tabchip${filtros.saude === aba.id ? " active" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => aplicarFiltro("saude", aba.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  aplicarFiltro("saude", aba.id);
                }
              }}
            >
              {aba.label}
            </div>
          ))}
        </div>
      </div>

      {/* Filtros extras (toggle) */}
      {filtrosAbertos && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <select
            className="select"
            value={filtros.tipo}
            onChange={(e) => aplicarFiltro("tipo", e.target.value)}
            aria-label="Filtrar por tipo"
          >
            <option value="">Todos os tipos</option>
            <option value="PF">Pessoa física</option>
            <option value="PJ">Pessoa jurídica</option>
          </select>
          <select
            className="select"
            value={filtros.status}
            onChange={(e) => aplicarFiltro("status", e.target.value)}
            aria-label="Filtrar por status"
          >
            <option value="ativos">Só ativos</option>
            <option value="bloqueados">Bloqueados</option>
            <option value="inativos">Inativos</option>
            <option value="todos">Todos</option>
          </select>
          {(filtros.tipo || filtros.status !== "ativos") && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                aplicarFiltro("tipo", "");
                aplicarFiltro("status", "ativos");
              }}
            >
              Limpar
            </button>
          )}
        </div>
      )}

      {estado.error && (
        <div className="so-banner danger" role="alert" style={{ marginBottom: 12 }}>
          <FiAlertCircle aria-hidden="true" /> {estado.error}
        </div>
      )}

      {/* Tabela */}
      <div className="table-wrap">
        <table className="dtable">
          <colgroup>
            <col style={{ width: "54px" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "104px" }} />
            <col />
            <col style={{ width: "120px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "124px" }} />
          </colgroup>
          <thead>
            <tr>
              <th aria-label="Saúde" />
              <th>Cliente</th>
              <th>Health</th>
              <th>Último contato</th>
              <th>Pedidos</th>
              <th className="num" style={{ textAlign: "right" }}>
                LTV
              </th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {estado.loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--ink-3)", padding: 24 }}>
                  Carregando clientes…
                </td>
              </tr>
            ) : estado.clientes.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--ink-3)", padding: 24 }}>
                  <FiUsers aria-hidden="true" style={{ verticalAlign: "middle", marginRight: 6 }} />
                  Nenhum cliente com esses filtros.
                </td>
              </tr>
            ) : (
              estado.clientes.map((c) => (
                <tr
                  key={c.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/clientes/${c.id}`)}
                  title={c.nome}
                >
                  <td style={{ textAlign: "center" }}>
                    <HealthRing score={c.health_score} tier={c.health_tier} size={32} />
                  </td>
                  <td>
                    <div className="entity-row">
                      <div
                        className="avatar-sm"
                        style={{ background: avatarGradiente(c.nome), color: "white" }}
                      >
                        {c.iniciais || "?"}
                      </div>
                      <div className="entity-meta">
                        <div className="name">
                          {c.nome}
                          {/* O inativo anonimizado é o único que não volta:
                              a linha precisa dizer qual é qual (CRM-10). */}
                          {c.esta_anonimizado ? (
                            <span className="badge neutral no-dot" style={{ marginLeft: 6, fontSize: 10 }}>
                              Anonimizado
                            </span>
                          ) : null}
                        </div>
                        <div className="sub">{subContato(c)}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ overflow: "visible" }}>
                    <HealthBadge tier={c.health_tier} />
                  </td>
                  <td>{c.ultimo_contato || "—"}</td>
                  <td>
                    <PedidosCell cliente={c} />
                  </td>
                  <td className={`num${c.ltv ? "" : " muted"}`}>{fmtBRLCheio(c.ltv)}</td>
                  <td style={{ overflow: "visible" }}>
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <SlideOverPanel
        open={novoAberto}
        onClose={() => setNovoAberto(false)}
        title="Novo cliente"
        subtitle="Preencha os dados básicos. Parceiro, marcadores e limite ficam em Comercial."
        width="min(95vw, 640px)"
      >
        <ClienteForm
          cliente={null}
          loading={salvando}
          buscarCep={(cep) => buscarCep(request, cep)}
          carregarParceiros={carregarParceiros}
          consultarContatoExistente={(params) => possiveisDuplicados(request, params)}
          onSubmit={handleCriar}
          onCancel={() => setNovoAberto(false)}
        />
      </SlideOverPanel>
    </PageContainer>
  );
}

export default ClientesLista;
